'use client';

import { useState } from 'react';

import { useParams } from 'next/navigation';

import { zodResolver } from '@hookform/resolvers/zod';
import { handleSubmitContactForm } from 'actions/submit-contact-form';

import { useForm } from 'react-hook-form';

import { Button } from '@repo/ui/button';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@repo/ui/form';
import Icon from '@repo/ui/icon';
import { Input } from '@repo/ui/input';
import { Textarea } from '@repo/ui/textarea';

import { handleConvertObjectToFormData } from 'helpers/form';

import { useToast } from 'hooks/use-toast';
import { useTranslation } from 'hooks/use-translation';

import { contactFormSchema, TContactForm } from './contact-form.types';

import { styles } from '.';

const ContactForm = () => {
  const [isPending, setIsPending] = useState(false);

  const { locale }: { locale: string } = useParams();

  const { t } = useTranslation(locale);
  const { toast } = useToast();

  const defaultValues: TContactForm = {
    contactFirstName: '',
    contactLastName: '',
    contactEmailAddress: '',
    contactTelephoneNumber: '',
    contactMessage: '',
  };

  const form = useForm<TContactForm>({
    resolver: zodResolver(contactFormSchema),
    defaultValues,
  });

  const onSubmit = async (values: TContactForm) => {
    setIsPending(true);

    const formData = handleConvertObjectToFormData(values);

    const result: any = await handleSubmitContactForm(formData, {
      locale,
    });

    if (result.status === 'error') {
      /** Disable log on production for security purposes. */
    }

    toast(result.message);
    form.reset();
    setIsPending(false);
  };

  return (
    <div className={styles.container}>
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className={styles.content}>
          {/* Contact First name */}
          <FormField
            name="contactFirstName"
            control={form.control}
            render={({ field }) => (
              <FormItem>
                <FormLabel className={styles.label}>
                  {t('component.form.label.contactFirstName')} *
                </FormLabel>
                <FormControl>
                  <Input
                    type="text"
                    // placeholder={t(
                    //   'component.form.placeholder.contactFirstName',
                    // )}
                    {...field}
                  />
                </FormControl>
                <div className={styles.foot}>
                  <FormMessage className={styles.error} t={t} />
                </div>
              </FormItem>
            )}
            disabled={isPending}
          />

          {/* Contact Last name */}
          <FormField
            name="contactLastName"
            control={form.control}
            render={({ field }) => (
              <FormItem>
                <FormLabel className={styles.label}>
                  {t('component.form.label.contactLastName')} *
                </FormLabel>
                <FormControl>
                  <Input
                    type="text"
                    // placeholder={t(
                    //   'component.form.placeholder.contactLastName',
                    // )}
                    {...field}
                  />
                </FormControl>
                <div className={styles.foot}>
                  <FormMessage className={styles.error} t={t} />
                </div>
              </FormItem>
            )}
            disabled={isPending}
          />

          {/* Contact Email address */}
          <FormField
            name="contactEmailAddress"
            control={form.control}
            render={({ field }) => (
              <FormItem>
                <FormLabel className={styles.label}>
                  {t('component.form.label.contactEmailAddress')} *
                </FormLabel>
                <FormControl>
                  <Input
                    type="text"
                    // placeholder={t(
                    //   'component.form.placeholder.contactEmailAddress',
                    // )}
                    {...field}
                  />
                </FormControl>
                <div className={styles.foot}>
                  <FormMessage className={styles.error} t={t} />
                </div>
              </FormItem>
            )}
            disabled={isPending}
          />

          {/* Contact Telephone number */}
          <FormField
            name="contactTelephoneNumber"
            control={form.control}
            render={({ field }) => (
              <FormItem>
                <FormLabel className={styles.label}>
                  {t('component.form.label.contactTelephoneNumber')}
                </FormLabel>
                <FormControl>
                  <Input
                    type="text"
                    // placeholder={t(
                    //   'component.form.placeholder.contactTelephoneNumber',
                    // )}
                    {...field}
                  />
                </FormControl>
                <div className={styles.foot}>
                  <FormMessage className={styles.error} t={t} />
                </div>
              </FormItem>
            )}
            disabled={isPending}
          />

          {/* Contact Message */}
          <FormField
            name="contactMessage"
            control={form.control}
            render={({ field }) => (
              <FormItem>
                <FormLabel className={styles.label}>
                  {t('component.form.label.contactMessage')} *
                </FormLabel>
                <FormControl>
                  <Textarea
                    // type="text"
                    // placeholder={t('component.form.placeholder.contactMessage')}
                    {...field}
                  />
                </FormControl>
                <div className={styles.foot}>
                  <FormMessage className={styles.error} t={t} />
                </div>
              </FormItem>
            )}
            disabled={isPending}
          />

          {/* Submit button */}
          <Button
            disabled={isPending}
            className={styles.submit.button}
            type="submit"
          >
            {isPending && (
              <div className={styles.submit.content}>
                <Icon accessor="spinner" className=" animate-spin" />
                <span>{t('label.data.isSubmitting')}</span>
              </div>
            )}
            {!isPending && (
              <div className={styles.submit.content}>
                <span>{t('label.data.submit')}</span>
              </div>
            )}
          </Button>
        </form>
      </Form>
    </div>
  );
};

export default ContactForm;
